import { FieldModel, TraitId } from '@cibo/core'
import Done from '@mui/icons-material/Done'
import Error from '@mui/icons-material/Error'
import HelpOutline from '@mui/icons-material/HelpOutline'
import LockIcon from '@mui/icons-material/Lock'
import { Tooltip } from '@mui/material'
import { GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { filter, flatten, propEq } from 'ramda'
import { useTranslation } from 'react-i18next'
import { BenchmarkStatusReasons } from '../BenchmarkStatus/BenchmarkStatusReasons'

enum Status {
  fail = 0,
  incomplete = 1,
  pass = 2,
}

export const useFieldProgramTraitReasonsColumn = ({
  programIds,
  traitId,
}: {
  programIds?: string[]
  traitId?: TraitId
}) => {
  const { t } = useTranslation('@cibo/programs')

  if (!programIds || !traitId) {
    return undefined
  }

  return {
    field: 'traitStatus',
    filterable: false,
    hideable: false,
    headerName: t('status'),
    type: 'string',
    valueGetter: (params: GridValueGetterParams<string, FieldModel>) => {
      const lockedDetails = params.row.details
        .filter(d => d.traitId === traitId)
        ?.filter(({ immutable }) => !!immutable)

      const reasons = filter(
        propEq('traitId', traitId),
        // @ts-ignore
        flatten(programIds.map(params.row.reasons))
      )

      return {
        locked: lockedDetails.length > 0,
        reasons,
        status:
          reasons.length === 0
            ? Status.pass
            : // @ts-ignore
            reasons.every(propEq('reason', 'missingData'))
            ? Status.incomplete
            : Status.fail,
      }
    },
    // @ts-ignore
    sortComparator: (v1, v2) => v1.status - v2.status,
    renderCell: (params: GridRenderCellParams<any, FieldModel & { locked: boolean }>) => {
      const { reasons, status, locked } = params.value

      const lockIcon = locked ? <LockIcon color="disabled" /> : null

      if (status === Status.pass) {
        return (
          <>
            <Done color="success" sx={{ margin: 2 }} />
            {lockIcon}
          </>
        )
      }
      if (status === Status.incomplete) {
        return (
          <>
            <Tooltip title={<BenchmarkStatusReasons reasons={reasons} />} placement="top" arrow>
              <HelpOutline color="warning" sx={{ margin: 2 }} />
            </Tooltip>
            {lockIcon}
          </>
        )
      }
      if (status === Status.fail) {
        return (
          <>
            <Tooltip title={<BenchmarkStatusReasons reasons={reasons} />} placement="top" arrow>
              <Error color="error" sx={{ margin: 2 }} />
            </Tooltip>
            {lockIcon}
          </>
        )
      }
    },
  }
}
